/**
 * 티켓 상세를 **모달로** 띄운다(2026-08-26 지시).
 *
 * **보드를 떠나지 않는 것이 요점이다.** 페이지로 이동하면 돌아올 때 보드가
 * 다시 그려지고 스크롤·필터가 처음 자리로 간다 — 카드 열댓 장을 훑는 동안
 * 그 왕복이 반복된다.
 *
 * ⚠ **`/tickets/:id` 페이지는 2026-08-29 에 걷었다**(지시: 상세는 무조건
 * 모달). 그래서 이 컴포넌트가 상세를 여는 **유일한 길**이다 — 보드·배포
 * 보드·배포 기록·GNB 검색 넷이 모두 이것을 쓴다.
 *
 * 티켓을 주소로 남에게 보내는 길은 2026-09-01 에 그쪽으로 되살렸다 —
 * `/tickets/:id` 가 **보드를 그리면서 이 모달을 연다**(`BoardPage`).
 * 주소를 만드는 자리는 상세 머리의 「링크 복사」다(`ticket/link.ts`).
 */

import { useCallback, useEffect, useState } from 'react';

import type { Project, TicketCard, User } from '../api';
import { Modal } from '../ui/Modal';
import { TicketDetail } from './TicketDetail';

export function TicketDialog({
  ticketId,
  user,
  projects,
  onError,
  onUnauthorized,
  onClose,
  onChanged,
  onDeleted,
}: {
  ticketId: number | null;
  user: User;
  projects: Project[];
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
  onClose: () => void;
  onChanged: (card: TicketCard) => void;
  onDeleted: (id: number) => void;
}) {
  /* 안 저장한 제목·설명이 있나(2026-10-18 UI 회의). 있으면 닫기 전에 묻는다 —
     옛 판은 바깥을 누르거나 Esc 를 치면 쓰던 글이 말없이 사라졌다. */
  const [dirty, setDirty] = useState(false);
  const guardedClose = useCallback(() => {
    if (dirty && !window.confirm('저장하지 않은 제목·설명이 있다. 닫을까?')) return;
    setDirty(false);
    onClose();
  }, [dirty, onClose]);

  // 새로고침·탭 닫기도 같은 글을 잃는다 — 브라우저 기본 확인을 띄운다.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return (
    <Modal
      open={ticketId !== null}
      onClose={guardedClose}
      // 화면에는 안 그린다 — 안에 티켓 제목이 이미 크게 있다.
      title="티켓 상세"
      /* **설명이 아니라 조작 안내다.** 「수정」 단추를 걷은 뒤로는
         두 번 누르는 것이 편집으로 들어가는 유일한 길이라, 이 한 줄이
         없으면 고칠 수 있다는 것 자체가 안 보인다. */
      description="「고치기」를 누르거나 고칠 곳을 두 번 누른다"
      size="xl"
      /* **머리와 아래 단추를 붙박이로**(2026-08-30 지시). 속성이 열넷이라
         모달이 늘 스크롤되는데, 옛 판은 그때 제목이 위로 밀려 무슨 티켓을
         보고 있었는지 사라졌다. */
      frame
    >
      {ticketId !== null && (
        <TicketDetail
          key={ticketId}
          ticketId={ticketId}
          user={user}
          projects={projects}
          onError={onError}
          onUnauthorized={onUnauthorized}
          onClose={onClose}
          onChanged={onChanged}
          onDeleted={onDeleted}
          onDirtyChange={setDirty}
        />
      )}
    </Modal>
  );
}
